import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Briefcase,
  Building2,
  Calendar,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  Mail,
  MessageSquare,
  Phone,
  Send,
  User,
} from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { apiClient } from '../lib/api'
import { formatRelative } from '../lib/utils'
import { Link } from '../router'
import { HistorySection } from './EntityHistory'
import {
  Badge,
  clientStatusVariant,
  labelForProposalStatus,
  projectStatusVariant,
  proposalStatusVariant,
} from './ui/Badge'
import { Button } from './ui/Button'
import { Drawer } from './ui/Drawer'
import { Skeleton } from './ui/Empty'
import { Input, Label, Select, Textarea } from './ui/Input'

type Client = {
  id: string
  name: string
  legalName: string | null
  status: string
  vatNumber: string | null
  fiscalCode: string | null
  pec: string | null
  email: string | null
  phone: string | null
  website: string | null
  addressLine1: string | null
  city: string | null
  province: string | null
  postalCode: string | null
  country: string | null
  notes: string | null
}

type Contact = {
  id: string
  fullName: string
  role: string | null
  email: string | null
  phone: string | null
  isPrimary: boolean
}

type Project = {
  id: string
  name: string
  status: string
  description: string | null
  startDate: string | null
  endDate: string | null
}

type ClientDetail = {
  found: boolean
  client?: Client
  contacts?: Contact[]
  activeProjects?: Project[]
}

type Interaction = {
  id: string
  type: 'email' | 'call' | 'chat' | 'meeting' | 'message' | 'note' | 'other'
  direction: 'inbound' | 'outbound' | 'internal' | null
  subject: string | null
  summary: string
  occurredAt: string
}

type InteractionList = { results: Interaction[]; total: number }

type ProposalSummary = {
  id: string
  code: string
  revision: number
  type: 'PRV' | 'CTR' | 'DPA'
  title: string
  subtitle: string | null
  status: string
  totalAmountEur: string | null
  sentAt: string | null
  acceptedAt: string | null
  updatedAt: string
}

type ProposalListResult = { results: ProposalSummary[]; total: number }

const TYPE_ICON: Record<Interaction['type'], typeof Mail> = {
  email: Mail,
  call: Phone,
  chat: MessageSquare,
  meeting: Calendar,
  message: MessageSquare,
  note: MessageSquare,
  other: MessageSquare,
}

export function ClientDetailDrawer({
  clientId,
  onClose,
}: {
  clientId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => apiClient.get<ClientDetail>(`/clients/${clientId}`),
  })

  const interactionsQuery = useQuery({
    queryKey: ['interactions', clientId],
    queryFn: () => apiClient.get<InteractionList>(`/interactions?clientId=${clientId}&limit=200`),
  })

  // Documents (PRV + CTR + future DPA) for this client. Includes superseded
  // revisions so the user sees the full history, not just the latest.
  const proposalsQuery = useQuery({
    queryKey: ['proposals', 'byClient', clientId],
    queryFn: () =>
      apiClient.get<ProposalListResult>(
        `/proposals?clientId=${clientId}&limit=100&includeSuperseded=true`,
      ),
  })

  const addInteraction = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.post('/interactions', { ...body, clientId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interactions', clientId] })
    },
  })

  if (isLoading || !data) {
    return (
      <Drawer open onClose={onClose} title="Cliente" width="lg">
        <div className="p-5">
          <Skeleton className="h-6 w-1/2 mb-3" />
          <Skeleton className="h-4 w-2/3 mb-2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </Drawer>
    )
  }
  if (!data.found || !data.client) {
    return (
      <Drawer open onClose={onClose} title="Cliente" width="lg">
        <div className="p-5 text-sm text-zinc-400">Cliente non trovato.</div>
      </Drawer>
    )
  }

  const c = data.client
  const contacts = data.contacts ?? []
  const projects = data.activeProjects ?? []
  const interactions = interactionsQuery.data?.results ?? []

  return (
    <Drawer
      open
      onClose={onClose}
      title={c.name}
      subtitle={
        c.legalName && c.legalName !== c.name ? (
          <div className="text-xs text-zinc-400">{c.legalName}</div>
        ) : undefined
      }
      badges={
        <>
          <Badge variant={clientStatusVariant(c.status)}>{c.status}</Badge>
          {c.vatNumber && <Badge variant="muted">P.IVA {c.vatNumber}</Badge>}
        </>
      }
      width="lg"
    >
      <div className="p-5 space-y-6">
        {/* Anagrafica */}
        <section>
          <SectionTitle icon={Building2}>Anagrafica</SectionTitle>
          <Grid>
            {c.vatNumber && <KV k="P.IVA" v={c.vatNumber} />}
            {c.fiscalCode && <KV k="Codice fiscale" v={c.fiscalCode} />}
            {c.pec && <KV k="PEC" v={c.pec} />}
            {c.email && <KV k="Email" v={c.email} icon={Mail} />}
            {c.phone && <KV k="Telefono" v={c.phone} icon={Phone} />}
            {c.website && <KV k="Sito" v={c.website} icon={Globe} />}
            {(c.addressLine1 || c.city) && (
              <div className="col-span-2">
                <dt className="text-xs text-zinc-500">Indirizzo</dt>
                <dd className="text-sm text-zinc-200">
                  {[c.addressLine1, c.postalCode, c.city, c.province, c.country]
                    .filter(Boolean)
                    .join(', ')}
                </dd>
              </div>
            )}
          </Grid>
          {c.notes && (
            <div className="mt-3">
              <dt className="text-xs text-zinc-500">Note</dt>
              <dd className="text-sm text-zinc-200 whitespace-pre-wrap">{c.notes}</dd>
            </div>
          )}
        </section>

        {/* Contatti */}
        {contacts.length > 0 && (
          <section>
            <SectionTitle icon={User}>Contatti ({contacts.length})</SectionTitle>
            <div className="space-y-2">
              {contacts.map((ct) => (
                <div
                  key={ct.id}
                  className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-100">{ct.fullName}</span>
                    {ct.isPrimary && <Badge variant="info">primary</Badge>}
                    {ct.role && <span className="text-xs text-zinc-500">· {ct.role}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-3">
                    {ct.email && <span>{ct.email}</span>}
                    {ct.phone && <span>{ct.phone}</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Progetti */}
        {projects.length > 0 && (
          <section>
            <SectionTitle icon={Briefcase}>Progetti ({projects.length})</SectionTitle>
            <div className="space-y-2">
              {projects.map((p) => (
                <div
                  key={p.id}
                  className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-zinc-100 truncate">{p.name}</div>
                    <Badge variant={projectStatusVariant(p.status)}>{p.status}</Badge>
                  </div>
                  {p.description && (
                    <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{p.description}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Documenti — PRV / CTR (in futuro anche DPA) collegati al cliente */}
        <DocumentsSection
          clientId={c.id}
          loading={proposalsQuery.isLoading}
          documents={proposalsQuery.data?.results ?? []}
          total={proposalsQuery.data?.total ?? 0}
        />

        {/* Timeline interazioni */}
        <section>
          <SectionTitle icon={MessageSquare}>
            Interazioni ({interactionsQuery.data?.total ?? 0})
          </SectionTitle>

          <NewInteractionForm
            onSubmit={(body) => addInteraction.mutate(body)}
            busy={addInteraction.isPending}
          />
          {addInteraction.isError && (
            <div className="text-xs text-red-400 mt-1">
              {(addInteraction.error as Error)?.message ?? 'Errore'}
            </div>
          )}

          <div className="mt-4 space-y-3">
            {interactionsQuery.isLoading ? (
              <Skeleton className="h-12 w-full" />
            ) : interactions.length === 0 ? (
              <p className="text-xs text-zinc-500">
                Nessuna interazione registrata. Usa il form sopra per la prima.
              </p>
            ) : (
              interactions.map((i) => {
                const Icon = TYPE_ICON[i.type] ?? MessageSquare
                return (
                  <div
                    key={i.id}
                    className="flex gap-3 rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2"
                  >
                    <Icon className="size-4 text-zinc-500 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-zinc-500">
                          <span className="uppercase tracking-wider">{i.type}</span>
                          {i.direction && <span> · {i.direction}</span>}
                          <span> · {formatRelative(i.occurredAt)}</span>
                        </div>
                      </div>
                      {i.subject && (
                        <div className="text-sm font-medium text-zinc-100 mt-1">{i.subject}</div>
                      )}
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap mt-1">{i.summary}</p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </section>

        {/* History */}
        <HistorySection entityType="client" entityId={c.id} />
      </div>
    </Drawer>
  )
}

function NewInteractionForm({
  onSubmit,
  busy,
}: {
  onSubmit: (body: Record<string, unknown>) => void
  busy: boolean
}) {
  const [open, setOpen] = useState(false)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const body: Record<string, unknown> = {}
    for (const [k, v] of fd.entries()) {
      const s = String(v).trim()
      if (s) body[k] = s
    }
    onSubmit(body)
    e.currentTarget.reset()
    setOpen(false)
  }

  if (!open) {
    return (
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Send className="size-3.5" /> Nuova interazione
      </Button>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 space-y-2"
    >
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label htmlFor="type">Tipo</Label>
          <Select id="type" name="type" defaultValue="note">
            <option value="email">Email</option>
            <option value="call">Call</option>
            <option value="meeting">Meeting</option>
            <option value="chat">Chat</option>
            <option value="message">Messaggio</option>
            <option value="note">Nota</option>
            <option value="other">Altro</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="direction">Direzione</Label>
          <Select id="direction" name="direction" defaultValue="">
            <option value="">—</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="internal">Internal</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="occurredAt">Data/ora</Label>
          <Input
            id="occurredAt"
            name="occurredAt"
            type="datetime-local"
            defaultValue={new Date().toISOString().slice(0, 16)}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="subject">Oggetto</Label>
        <Input id="subject" name="subject" />
      </div>
      <div>
        <Label htmlFor="summary">Riassunto *</Label>
        <Textarea id="summary" name="summary" required minLength={3} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Annulla
        </Button>
        <Button type="submit" size="sm" disabled={busy}>
          <Send className="size-3.5" /> {busy ? 'Salvataggio…' : 'Salva interazione'}
        </Button>
      </div>
    </form>
  )
}

function DocumentsSection({
  clientId,
  loading,
  documents,
  total,
}: {
  clientId: string
  loading: boolean
  documents: ProposalSummary[]
  total: number
}) {
  // Sort: most recently updated first. Keep all revisions visible (superseded
  // included) so the user has the full history at a glance.
  const sorted = [...documents].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))

  const headerCount = total > documents.length ? `${documents.length}/${total}` : String(total)

  return (
    <section>
      <SectionTitle icon={FileText}>Documenti ({headerCount})</SectionTitle>
      {loading ? (
        <Skeleton className="h-12 w-full" />
      ) : sorted.length === 0 ? (
        <p className="text-xs text-zinc-500">
          Nessun preventivo o contratto collegato a questo cliente.{' '}
          <Link to="/proposals" className="text-zinc-300 underline-offset-2 hover:underline">
            Vai a /proposals
          </Link>{' '}
          per crearne uno nuovo.
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map((d) => (
            <DocumentRow key={d.id} doc={d} />
          ))}
          {sorted.length > 0 && (
            <div className="pt-1">
              <Link
                to={`/proposals?clientId=${clientId}`}
                className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
              >
                <ExternalLink className="size-3" /> Apri tutti in /proposals
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function DocumentRow({ doc }: { doc: ProposalSummary }) {
  const importo =
    doc.totalAmountEur != null && doc.totalAmountEur !== ''
      ? `€ ${Number(doc.totalAmountEur).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null

  const previewHref = `/api/proposals/${doc.id}/preview`

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={doc.type === 'CTR' ? 'brand' : doc.type === 'DPA' ? 'success' : 'info'}>
              {doc.type}
            </Badge>
            <span className="text-sm font-medium text-zinc-100">
              {doc.code}
              {doc.revision > 0 && <span className="text-zinc-500"> R{doc.revision}</span>}
            </span>
            <Badge variant={proposalStatusVariant(doc.status)}>
              {labelForProposalStatus(doc.status)}
            </Badge>
            {importo && <span className="text-xs text-zinc-400">· {importo}</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 truncate">{doc.title}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={previewHref}
            target="_blank"
            rel="noopener noreferrer"
            title="Anteprima documento"
            className="p-1.5 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          >
            <Eye className="size-4" />
          </a>
          <Link
            to={`/proposals?focus=${doc.code}`}
            title="Apri in /proposals"
            className="p-1.5 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          >
            <ExternalLink className="size-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}

function SectionTitle({
  icon: Icon,
  children,
}: {
  icon: typeof Building2
  children: React.ReactNode
}) {
  return (
    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
      <Icon className="size-3.5" /> {children}
    </h3>
  )
}

function Grid({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">{children}</dl>
}

function KV({ k, v, icon: Icon }: { k: string; v: string; icon?: typeof Mail }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-zinc-500">{k}</dt>
      <dd className="text-zinc-200 truncate flex items-center gap-1.5">
        {Icon && <Icon className="size-3.5 text-zinc-500" />} {v}
      </dd>
    </div>
  )
}
